import Box from "@mui/material/Box";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import TableContainer from "../../../components/Tables/TableContainer";
import TableCell from "../../BillingAndPayments/components/TableCell";
import Button from "../../../components/Buttons/Button";
import Loader from "../../../components/Loaders/Loader";
import { useTranslationApp } from "../../../lib/i18next";
import {
  BUTTON_REVIEW_CONTENT,
  COLUMN_NAME_ACTIONS,
  COLUMN_NAME_ADVERTISER,
  COLUMN_NAME_AGENCY,
  COLUMN_NAME_CREATED_DATE,
  COLUMN_NAME_REASON,
  COLUMN_NAME_STATUS,
  COLUMN_NAME_USER,
  DATE_SHORT,
  EMPTY_RESULTS_SHOW,
  REPORTED_CONTENT_STATUS_APPROVED,
  REPORTED_CONTENT_STATUS_PENDING,
  REPORTED_CONTENT_STATUS_REJECTED,
} from "../../../i18n/keysTranslations";
import { REPORTED_CONTENT_STATUSES } from "../../../utils/constants";

const headerCellProps = { showTooltip: false, isHeader: true };
const bodyCellProps = {
  showTooltip: false,
  isHeader: false,
  showLastRowBorder: true,
};

const firstColSx = {
  paddingLeft: "16px !important",
};

const formatCreated = ({ creationTime, t }) => {
  if (creationTime == null || Number.isNaN(Number(creationTime))) return "—";
  try {
    return t(DATE_SHORT, { date: new Date(Number(creationTime)) });
  } catch {
    return "—";
  }
};

const getUserText = (row) => {
  if (row?.userName && row?.userEmail) {
    return `${row.userName} (${row.userEmail})`;
  }
  return row?.userName || row?.userEmail || row?.userID || "—";
};

const REPORTED_CONTENT_STATUS_TRANSLATION_MAP = {
  [REPORTED_CONTENT_STATUSES.REJECTED]: REPORTED_CONTENT_STATUS_REJECTED,
  [REPORTED_CONTENT_STATUSES.PENDING]: REPORTED_CONTENT_STATUS_PENDING,
  [REPORTED_CONTENT_STATUSES.APPROVED]: REPORTED_CONTENT_STATUS_APPROVED,
};

const REPORTED_CONTENT_STATUS_SX_MAP = {
  [REPORTED_CONTENT_STATUSES.REJECTED]: {
    bgcolor: "rgba(244, 67, 54, 0.1)",
    border: "1px solid rgba(244, 67, 54, 0.3)",
  },
  [REPORTED_CONTENT_STATUSES.PENDING]: {
    bgcolor: "rgba(255, 193, 7, 0.1)",
    border: "1px solid rgba(255, 193, 7, 0.3)",
  },
  [REPORTED_CONTENT_STATUSES.APPROVED]: {
    bgcolor: "rgba(76, 175, 80, 0.1)",
    border: "1px solid rgba(76, 175, 80, 0.3)",
  },
};

const ReportedContentStatusBadge = ({ status }) => {
  const { t } = useTranslationApp();
  const statusValue = status?.toLowerCase?.();
  const statusTranslationKey =
    REPORTED_CONTENT_STATUS_TRANSLATION_MAP[statusValue];

  if (!statusTranslationKey) {
    return "—";
  }

  return (
    <Typography
      variant="caption"
      sx={{
        px: 1.5,
        py: 0.5,
        borderRadius: 1,
        color: "#333333",
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        fontSize: "0.7rem",
        ...REPORTED_CONTENT_STATUS_SX_MAP[statusValue],
      }}
    >
      {t(statusTranslationKey)}
    </Typography>
  );
};

const ReportedContentRowActions = ({ row = {}, onViewContent = () => {} }) => {
  const { t } = useTranslationApp();

  return (
    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
      <Button
        color="secondary"
        size="small"
        disabled={!Array.isArray(row.urls) || row.urls.length === 0}
        onClick={() => onViewContent(row)}
      >
        {t(BUTTON_REVIEW_CONTENT)}
      </Button>
    </Box>
  );
};

const ReportedContentTable = ({
  rows = [],
  loading = false,
  loadingMore = false,
  hasMore = false,
  lastItemRef,
  onViewContent = () => {},
}) => {
  const { t } = useTranslationApp();

  return (
    <TableContainer
      loading={loading && rows.length === 0}
      keys={rows}
      hasPagination={false}
      emptyText={EMPTY_RESULTS_SHOW}
      ariaLabel="reported-content"
      sxContainer={{ maxHeight: "calc(100vh - 240px)" }}
    >
      <TableHead>
        <TableRow>
          <TableCell {...headerCellProps} sx={firstColSx}>
            {t(COLUMN_NAME_AGENCY)}
          </TableCell>
          <TableCell {...headerCellProps}>
            {t(COLUMN_NAME_CREATED_DATE)}
          </TableCell>
          <TableCell {...headerCellProps}>
            {t(COLUMN_NAME_ADVERTISER)}
          </TableCell>
          <TableCell {...headerCellProps}>{t(COLUMN_NAME_USER)}</TableCell>
          <TableCell {...headerCellProps}>{t(COLUMN_NAME_REASON)}</TableCell>
          <TableCell {...headerCellProps}>{t(COLUMN_NAME_STATUS)}</TableCell>
          <TableCell {...headerCellProps}>{t(COLUMN_NAME_ACTIONS)}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row, index) => {
          const isLastRow = index === rows.length - 1 && !hasMore;
          return (
            <TableRow key={row.key}>
              <TableCell
                {...bodyCellProps}
                isLastRow={isLastRow}
                sx={firstColSx}
              >
                {row.agencyName || row.agencyID || "—"}
              </TableCell>
              <TableCell {...bodyCellProps} isLastRow={isLastRow}>
                {formatCreated({ creationTime: row.creationTime, t })}
              </TableCell>
              <TableCell {...bodyCellProps} isLastRow={isLastRow}>
                {row.advertiserName || row.advertiserID || "—"}
              </TableCell>
              <TableCell {...bodyCellProps} isLastRow={isLastRow}>
                {getUserText(row)}
              </TableCell>
              <TableCell {...bodyCellProps} isLastRow={isLastRow}>
                {row.reason || "—"}
              </TableCell>
              <TableCell {...bodyCellProps} isLastRow={isLastRow}>
                <ReportedContentStatusBadge status={row?.status} />
              </TableCell>
              <TableCell {...bodyCellProps} isLastRow={isLastRow}>
                <ReportedContentRowActions
                  row={row}
                  onViewContent={onViewContent}
                />
              </TableCell>
            </TableRow>
          );
        })}
        {hasMore && !loading && (
          <TableRow
            ref={lastItemRef}
            sx={{
              "&:hover": {
                backgroundColor: "transparent !important",
              },
            }}
          >
            <TableCell
              colSpan={7}
              align="center"
              showTooltip={false}
              isHeader={false}
              sx={{
                minHeight: "80px !important",
                padding: "20px !important",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  width: "100%",
                  minHeight: 40,
                }}
              >
                {loadingMore ? <Loader size={24} hasMarginTop={false} /> : null}
              </Box>
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </TableContainer>
  );
};

export default ReportedContentTable;
